// src/services/pollinationsService.js

export const TEXT_API_URL = 'https://text.pollinations.ai/'; // URL base de la API de texto de Pollinations
export const DEFAULT_POLLINATIONS_TEXT_MODEL = 'openai-large';//'openai-fast'; // Modelo por defecto si no se especifica
// También puedes tener una función específica para la API de imágenes si es muy diferente
// o si quieres añadir más lógica (ej. manejo de errores de imagen)
// Por ahora, la URL base de imagen se usa directamente en los componentes.
export const POLLINATIONS_IMAGE_API_BASE = 'https://image.pollinations.ai/prompt/';
export const DEFAULT_POLLINATIONS_IMAGE_MODEL = 'flux';

/**
 * Parsea la respuesta de la API de Pollinations.
 * Intenta extraer JSON de forma robusta, incluso si está envuelto en texto.
 * @param {any} apiResponseData - La data cruda de la respuesta de la API.
 * @param {boolean} expectPlainText - Si es true, intentará devolver el contenido como texto si el parseo JSON falla.
 * @returns {object|string|null} El contenido parseado o el texto.
 * @throws {Error} Si el parseo falla y no se espera texto plano.
 */
const parsePollinationsApiResponse = (apiResponseData, expectPlainText = false) => {
    try {
        if (!apiResponseData) {
            throw new Error('No data received from API to parse.');
        }

        // Caso 1: La respuesta ya es un objeto (podría ser un error JSON de la API o ya parseado)
        if (typeof apiResponseData === 'object' && apiResponseData !== null) {
            if (apiResponseData.content) {
                // Si hay 'content', intentar parsear 'content'
                const content = apiResponseData.content;
                if (typeof content === 'object' && content !== null) return content; // Ya es un objeto
                if (typeof content === 'string') {
                    const jsonMatch = content.match(/\{[\s\S]*\}/); // Intenta encontrar JSON dentro del string
                    if (jsonMatch && jsonMatch[0]) {
                        try { return JSON.parse(jsonMatch[0]); } catch (e) { /* sigue a otros intentos */ }
                    }
                    try { return JSON.parse(content); } catch (e) { /* sigue */ }
                    if (expectPlainText) return content; // Si se esperaba texto y el parseo JSON falló
                }
            }
            // Si no hay 'content' pero es un objeto, devolverlo tal cual (podría ser un error estructurado)
            return apiResponseData;
        }

        // Caso 2: La respuesta es un string
        if (typeof apiResponseData === 'string') {
            const jsonMatch = apiResponseData.match(/\{[\s\S]*\}/);
            if (jsonMatch && jsonMatch[0]) {
                try { return JSON.parse(jsonMatch[0]); } catch (e) { /* sigue */ }
            }
            try { return JSON.parse(apiResponseData); } catch (e) { /* sigue */ }
            if (expectPlainText) return apiResponseData;
        }

        // Si todo falla y no se esperaba texto plano
        if (!expectPlainText) {
            console.warn('Failed to parse API response as JSON:', apiResponseData);
            throw new Error('Invalid JSON response format from API.');
        }
        // Si se esperaba texto plano y no se pudo parsear JSON, devolver como está (ya es string o se convirtió)
        return typeof apiResponseData === 'string' ? apiResponseData : String(apiResponseData);

    } catch (parseError) {
        console.error('Pollinations API Parse Error:', parseError.message, 'Original data:', apiResponseData);
        // Si se esperaba texto y el parseo falló, intenta devolver el contenido original si es un string
        if (expectPlainText && typeof apiResponseData?.content === 'string') {
            return apiResponseData.content;
        }
        if (expectPlainText && typeof apiResponseData === 'string') {
            return apiResponseData;
        }
        throw new Error(`Parse error: ${parseError.message}. Check API response structure.`);
    }
};


/**
 * Realiza una llamada a la API de texto de Pollinations.
 * @param {string} systemPrompt - El prompt del sistema.
 * @param {string|Array<object>} userContent - El contenido del usuario. Puede ser un string simple
 *                                            o un array para mensajes complejos (ej. con imágenes).
 * @param {object} options - Opciones adicionales para la llamada.
 * @param {string} [options.model=DEFAULT_POLLINATIONS_TEXT_MODEL] - El modelo a usar.
 * @param {boolean} [options.isJsonExpected=true] - Si se espera una respuesta JSON (afecta jsonMode y parseo).
 * @param {boolean} [options.isPrivate=true] - Parámetro 'private' para la API.
 * @param {number} [options.temperature] - Temperatura para la generación.
 * @param {number} [options.max_tokens] - Máximo de tokens.
 * @returns {Promise<object|string>} La respuesta parseada de la API.
 * @throws {Error} Si la petición API falla o el parseo de la respuesta falla.
 */
export const callPollinationsTextApi = async (
    systemPrompt,
    userContent = 'Generate.',
    options = {}
) => {
    const {
        model = DEFAULT_POLLINATIONS_TEXT_MODEL,
        isJsonExpected = true, // Por defecto esperamos JSON
        isPrivate = true,
        temperature,
        max_tokens
    } = options;

    // Construir el mensaje del usuario
    // Si userContent es un string, se envuelve. Si es un array, se usa directamente.
    const userMessageContent = typeof userContent === 'string' ? [{ type: 'text', text: userContent }] : userContent;

    const requestBody = {
        messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userMessageContent }
        ],
        model: model,
        jsonMode: isJsonExpected, // jsonMode se basa en si esperamos JSON
        private: isPrivate
    };

    // Añadir parámetros opcionales si están definidos
    if (temperature !== undefined) requestBody.temperature = temperature;
    if (max_tokens !== undefined) requestBody.max_tokens = max_tokens;

    console.log("Calling Pollinations API with body:", JSON.stringify(requestBody, null, 2).substring(0, 500) + "..."); // Loguear cuerpo de la petición

    let response;
    try {
        response = await fetch(TEXT_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // Aquí podrías añadir API keys si Pollinations las requiere en el futuro
            },
            body: JSON.stringify(requestBody)
        });
    } catch (networkError) {
        console.error('Pollinations API Network Error:', networkError);
        throw new Error(`Network error calling Pollinations API: ${networkError.message}`);
    }

    if (!response.ok) {
        let errorText = 'Could not retrieve error details.';
        try {
            errorText = await response.text();
        } catch (e) {
            console.warn("Could not read error text from API response.");
        }
        console.error("Pollinations API Error Response Text:", errorText);
        throw new Error(`Pollinations API request failed: ${response.status} ${response.statusText} - ${errorText.substring(0, 250)}`);
    }

    // Leer la respuesta. Si se espera JSON, intentar parsear como JSON.
    // Si se espera texto plano, leer como texto.
    let rawData;
    try {
        if (isJsonExpected) { // Si se espera JSON, la API debería devolver Content-Type: application/json
            rawData = await response.json();
        } else { // Si esperamos texto plano (ej. para una descripción)
            rawData = await response.text(); // Esto devolverá un string
            // Para el caso de texto plano, la función de parseo lo manejará si es envuelto en 'content'
            // o si es un string directo.
            return parsePollinationsApiResponse({ content: rawData }, true); // Envolver para que parseo funcione, y esperar texto
        }
    } catch (e) {
        console.error('Error reading API response body:', e);
        throw new Error(`Failed to read response body: ${e.message}`);
    }


    console.log('Raw data from Pollinations API:', rawData);
    return parsePollinationsApiResponse(rawData, !isJsonExpected);
};


